import {
  useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  getPermissionIdsFromAccessPayload,
  getAccessPayloadFromPermissionIds,
  getPermanentUserIdsFromAccessPayload,
} from './customExportAccess';

/**
 * Redux-backed state for custom export "who can view" assignees (positions + users)
 * and helpers to map to/from API `access` payloads.
 *
 * @param {object} selectedExport
 * @param {{
 *   userIds?: Array<{ userId: string, isPermanent: boolean }>,
 *   positionIds?: string[],
 * }} [selectedExport.access]
 * When editing, pass the row so initial TreeSelect values match `access`.
 */
export default function useCustomExportPermissions(selectedExport) {
  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);

  const [selectedPermissions, setSelectedPermissions] = useState(
    () => getPermissionIdsFromAccessPayload(selectedExport),
  );
  const [permanentUserIds, setPermanentUserIds] = useState(
    () => getPermanentUserIdsFromAccessPayload(selectedExport),
  );

  const positionIdSet = useMemo(
    () => new Set(positionNames.map((p) => p.id)),
    [positionNames],
  );
  const activeUserIdSet = useMemo(
    () => new Set(users.filter((u) => u.active).map((u) => u.id)),
    [users],
  );

  const buildAccessPayload = () => (
    getAccessPayloadFromPermissionIds(selectedPermissions, {
      positionIdSet,
      userIdSet: activeUserIdSet,
    })
  );

  const hydratePermissionsFromExport = () => {
    setSelectedPermissions(getPermissionIdsFromAccessPayload(selectedExport));
    setPermanentUserIds(getPermanentUserIdsFromAccessPayload(selectedExport));
  };

  const clearPermissions = () => {
    setSelectedPermissions([]);
    setPermanentUserIds([]);
  };

  return {
    selectedPermissions,
    permanentUserIds,
    buildAccessPayload,
    hydratePermissionsFromExport,
    clearPermissions,
    setSelectedPermissions,
  };
}
